import { useState, useContext, useEffect } from "react";
import BryntumContext from "@/react/context/bryntumContext";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { Button } from "@/modern/components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/modern/components/ui/dialog";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import { Eye, EyeOff, Trash2 } from "lucide-react";
import { getApiErrorMessage, isBoardSyncRequestError } from "@/integrations";
import { useHasOpenAIKey } from "@/react/hooks/useHasOpenAIKey";
import { useBoardPermissions } from "../hooks/useBoardPermissions";
import { removeBoardSecret, saveBoardSecret } from "../utils/boardSecretPreference";

interface OpenAIKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a key is saved successfully */
  onSaved?: () => void;
  /** When nested inside a Bryntum/BaseModal, stack above the parent modal */
  stackAboveModal?: boolean;
}

const ELEVATED_Z_INDEX = "z-[calc(var(--swivu-bryntum-z-index)+30)]";

export const OpenAIKeyModal = ({ isOpen, onClose, onSaved, stackAboveModal = false }: OpenAIKeyModalProps) => {
  const { project } = useContext(BryntumContext);
  const [apiKey, setApiKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const hasExistingKey = useHasOpenAIKey();
  const { canManageSecrets } = useBoardPermissions();

  useEffect(() => {
    if (isOpen) {
      setApiKey("");
      setShowPassword(false);
    }
  }, [isOpen]);

  const handleSaveApiKey = async () => {
    if (!project) return;

    if (!canManageSecrets) {
      toast.error("You don't have permission to update the OpenAI API key");
      return;
    }

    const trimmedKey = apiKey.trim();
    if (!trimmedKey) {
      toast.error("Please enter an API key or use the Disconnect button to remove it");
      return;
    }

    setIsSaving(true);
    try {
      await saveBoardSecret(project, PERSISTENCE_STATE_KEYS.openAiApiKey, trimmedKey);
      toast.success(hasExistingKey ? "OpenAI API key updated successfully" : "OpenAI API key saved successfully");
      onSaved?.();
      onClose();
    } catch (error) {
      if (!isBoardSyncRequestError(error)) {
        toast.error(getApiErrorMessage(error, "Failed to save OpenAI API key"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveKey = async () => {
    if (!project) return;

    if (!canManageSecrets) {
      toast.error("You don't have permission to remove the OpenAI API key");
      return;
    }

    if (!hasExistingKey) {
      toast.error("No API key to remove");
      return;
    }

    setIsSaving(true);
    try {
      await removeBoardSecret(project, PERSISTENCE_STATE_KEYS.openAiApiKey);
      toast.success("OpenAI API key removed successfully");
      onClose();
    } catch (error) {
      if (!isBoardSyncRequestError(error)) {
        toast.error(getApiErrorMessage(error, "Failed to remove OpenAI API key"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className={stackAboveModal ? `sm:max-w-[500px] ${ELEVATED_Z_INDEX}` : "sm:max-w-[500px]"}
        dialogOverlayClassName={stackAboveModal ? ELEVATED_Z_INDEX : undefined}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Configure OpenAI API Key</DialogTitle>
          <DialogDescription>Enter your OpenAI API key to enable AI-powered features in your board.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="input1" className="text-sm font-medium">
              OpenAI API Key
            </Label>
            <div className="relative">
              <Input
                id="input1"
                type={showPassword ? "text" : "password"}
                placeholder="sk-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={isSaving}
                autoComplete="one-time-code"
                data-form-type="other"
                className="font-mono text-sm pr-10"
              />
              <Button type="button" variant="ghost" size="icon-sm" className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setShowPassword(!showPassword)} disabled={isSaving}>
                {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {hasExistingKey ? "A key is connected. The saved key stays hidden. Enter a new key to replace it, or disconnect it below." : "No key is connected. Enter a key to save it."}
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-900 dark:text-blue-200 leading-relaxed">
              <strong>How to get your API key:</strong>
              <br />
              1. Visit{" "}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">
                platform.openai.com/api-keys
              </a>
              <br />
              2. Sign in or create an OpenAI account
              <br />
              3. Create a new API key and copy it here
            </p>
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong>Important:</strong> Keep your API key secure and never share it publicly. After it is saved it is not shown again. The Board Owner or a Board Admin can replace or disconnect it.
          </p>
        </div>

        <DialogFooter className="sm:justify-between">
          {hasExistingKey ? (
            <>
              <Button variant="destructive" onClick={handleRemoveKey} disabled={isSaving} size="sm" className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                Disconnect
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} disabled={isSaving} size="sm">
                  Cancel
                </Button>
                <Button variant="default" onClick={handleSaveApiKey} disabled={isSaving || !apiKey.trim()} size="sm">
                  {isSaving ? "Updating..." : "Replace Key"}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={onClose} disabled={isSaving} size="sm">
                Cancel
              </Button>
              <Button variant="default" onClick={handleSaveApiKey} disabled={isSaving || !apiKey.trim()} size="sm">
                {isSaving ? "Saving..." : "Save Key"}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
